import { useCallback, useEffect, useMemo, useState } from "react";
import * as localStorageService from "src/app/modules/localstorage";
import { firestoreMarketplaceAccountService } from "../services/firestoreMarketplaceAccountService";
import type { IMarketplaceAccountService } from "../services/IMarketplaceAccountService";
import type { MarketplaceAccount, MarketplaceActivityFilter, MarketplaceListRequest } from "../types/marketplaceAccount";

const LEGACY_STATE_KEY = "tableStateMarketplacelist";

export type MarketplaceListState = Omit<MarketplaceListRequest, "clientId">;

const initialState: MarketplaceListState = {
  activity: "all",
  marketplaceTypes: [],
  search: "",
  page: 1,
  pageSize: 20,
  sort: "updatedAt",
  sortDirection: "desc",
};

type UseMarketplaceAccountsOptions = { service?: IMarketplaceAccountService };

export function useMarketplaceAccounts({ service = firestoreMarketplaceAccountService }: UseMarketplaceAccountsOptions = {}) {
  const [clientId, setClientId] = useState(() => localStorageService.getClientId());
  const [state, setState] = useState<MarketplaceListState>(initialState);
  const [items, setItems] = useState<MarketplaceAccount[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const refresh = useCallback(() => setRefreshToken((value) => value + 1), []);
  const updateState = useCallback((change: Partial<MarketplaceListState>) => {
    setState((current) => {
      const next = { ...current, ...change, page: change.page ?? 1 };
      return Object.keys(next).every((key) => next[key as keyof MarketplaceListState] === current[key as keyof MarketplaceListState])
        ? current
        : next;
    });
  }, []);

  useEffect(() => {
    localStorage.removeItem(LEGACY_STATE_KEY);
    const syncClient = () => setClientId(localStorageService.getClientId());
    window.addEventListener("focus", syncClient);
    window.addEventListener("storage", syncClient);
    return () => {
      window.removeEventListener("focus", syncClient);
      window.removeEventListener("storage", syncClient);
    };
  }, []);

  useEffect(() => {
    setState((current) => current.page === 1 ? current : { ...current, page: 1 });
  }, [clientId]);

  useEffect(() => {
    if (!clientId) {
      setItems([]);
      setTotal(0);
      setError(null);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    service.listAccounts({ ...state, clientId })
      .then((result) => {
        if (!active) return;
        setItems(result.items);
        setTotal(result.total);
      })
      .catch((reason) => {
        if (!active) return;
        setItems([]);
        setTotal(0);
        setError(reason instanceof Error ? reason : new Error("Unable to load marketplace accounts"));
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [clientId, refreshToken, service, state]);

  return useMemo(() => ({
    clientId,
    state,
    items,
    total,
    loading,
    error,
    refresh,
    updateState,
  }), [clientId, error, items, loading, refresh, state, total, updateState]);
}
